import { Routes, Route, NavLink, Navigate, useLocation } from 'react-router-dom';
import {
  Loader2,
  LayoutDashboard,
  Users,
  FilePlus2,
  Landmark,
  LogOut,
  ShieldCheck,
  ShieldAlert,
} from 'lucide-react';

import { useAdminSessionState, AdminSessionContext, useAdmin } from './useAdminSession.js';
import { Login } from './Login.js';
import { Setup } from './Setup.js';
import { Customers } from './Customers.js';
import { CustomerEdit } from './CustomerEdit.js';
import { Dashboard } from './Dashboard.js';
import { RequestNew } from './RequestNew.js';
import { RequestDetail } from './RequestDetail.js';
import { Submissions } from './Submissions.js';

/**
 * The admin shell.
 *
 * Routes the four session states to four different screens rather than one
 * generic "denied": signed out, not on the allowlist, signed in without a
 * second factor, and ready.
 */
export function AdminApp() {
  const { state, refresh, signOut } = useAdminSessionState();
  const location = useLocation();

  if (state.status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" aria-label="Loading" />
      </div>
    );
  }

  // /admin/setup is the only route reachable without a session — it is where
  // an invite code is redeemed, which is how a session is obtained in the
  // first place.
  if (location.pathname.replace(/\/$/, '') === '/admin/setup') {
    return <Setup onDone={refresh} />;
  }

  if (state.status === 'signed-out') return <Login onSignedIn={refresh} />;

  // The second factor comes before the app. A session that reloaded the page
  // between the password and the code lands here too.
  if (state.status === 'challenge') return <Login onSignedIn={refresh} />;

  return (
    <AdminSessionContext.Provider value={{ me: state.me, refresh, signOut }}>
      <Shell />
    </AdminSessionContext.Provider>
  );
}

function Shell() {
  const { me, signOut } = useAdmin();

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center gap-6 px-6 py-3">
          <span className="font-bold tracking-tight text-gold-700">Gold Star Printing</span>

          <nav className="flex items-center gap-1">
            <Tab to="/admin" end icon={LayoutDashboard} label="Dashboard" />
            <Tab to="/admin/requests/new" icon={FilePlus2} label="New request" />
            <Tab to="/admin/customers" icon={Users} label="Customers" />
            <Tab to="/admin/submissions" icon={Landmark} label="Submissions" />
          </nav>

          <div className="ml-auto flex items-center gap-3">
            <MfaBadge canReveal={me.canRevealBankDetails} />
            <span className="text-sm text-slate-600">{me.displayName ?? me.email}</span>
            <button
              onClick={() => void signOut()}
              className="rounded p-1.5 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
              title="Sign out"
            >
              <LogOut className="h-4 w-4" aria-label="Sign out" />
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-8">
        <Routes>
          <Route index element={<Dashboard />} />
          <Route path="requests/new" element={<RequestNew />} />
          <Route path="requests/:id" element={<RequestDetail />} />
          <Route path="customers" element={<Customers />} />
          <Route path="customers/:id" element={<CustomerEdit />} />
          <Route path="submissions" element={<Submissions />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </main>
    </div>
  );
}

/**
 * Whether this session can reveal bank details.
 *
 * With the second factor now required before the app renders at all, this is
 * green in normal use. It stays because the underlying state is still
 * per-session — a recovery-code sign-in deliberately does NOT reach aal2 — and
 * seeing why the reveal button is refusing beats a 403 at the moment someone
 * needs the number.
 */
function MfaBadge({ canReveal }: { canReveal: boolean }) {
  return canReveal ? (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800"
      title="This session used two-factor, so bank details can be revealed."
    >
      <ShieldCheck className="h-3.5 w-3.5" aria-hidden />
      2FA
    </span>
  ) : (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800"
      title="Sign in again with your authenticator code to reveal bank details."
    >
      <ShieldAlert className="h-3.5 w-3.5" aria-hidden />
      2FA needed
    </span>
  );
}

function Tab({
  to,
  end,
  icon: Icon,
  label,
}: {
  to: string;
  end?: boolean;
  icon: typeof Users;
  label: string;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition ${
          isActive ? 'bg-gold-50 text-gold-800' : 'text-slate-600 hover:bg-slate-100'
        }`
      }
    >
      <Icon className="h-4 w-4" aria-hidden />
      {label}
    </NavLink>
  );
}

